import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";

// Same link-scanner hardening as PartnerWorkspaceConfirmSignIn.jsx: this page is a plain GET,
// safe for email link-scanners/preview-unfurlers to pre-fetch — it never consumes the token
// itself. Only the explicit "Confirm sign-in" button's POST (below) does.
const ERROR_COPY = {
  missing_token: "That link looks incomplete. Request a new one below.",
  invalid_or_expired: "That link has expired or was already used — links are single-use and valid for 15 minutes. Request a new one below.",
  no_access: "We couldn't find an approved NJM Build freelancer profile for this link. Contact NJM if this seems wrong.",
  account_conflict: "This email is linked to an existing account and can't be used for email sign-in. Contact NJM if this seems wrong.",
};

export default function BuildConfirmSignIn() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState(token ? "idle" : "missing"); // idle | confirming | error | missing
  const [errorCode, setErrorCode] = useState(token ? null : "missing_token");

  const confirm = async () => {
    setState("confirming");
    try {
      const { data } = await api.post("/v1/build/auth/magic-link/verify", { token });
      localStorage.setItem("njm_last_portal", "staff"); // 2026-09 security fix — clears any stale agency marker
      // Full navigation (not React Router) so AuthContext mounts fresh and picks up the
      // just-set session cookie via its own /auth/me check. Wave 3 (2026-09) — return-url: land
      // on `data.next` (the same-origin path the backend already re-validated) if the original
      // magic-link request captured one, else the same /build/me default as before this change.
      window.location.href = data?.next || "/build/me";
    } catch (err) {
      setErrorCode(err?.response?.data?.detail?.code || "invalid_or_expired");
      setState("error");
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-canvas px-4" data-testid="build-confirm-signin-page">
      <div className="w-full max-w-[380px]">
        <div className="flex justify-center mb-8"><Logo theme="light" size="lg" /></div>
        <div className="rounded-lg border border-hairline bg-white p-8 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-faint">NJM Build</div>

          {state === "error" ? (
            <div className="mt-4" data-testid="build-confirm-signin-error">
              <h1 className="font-editorial text-2xl text-ink mb-4">Can't sign you in</h1>
              <p className="text-sm text-ink-soft mb-6">{ERROR_COPY[errorCode] || ERROR_COPY.invalid_or_expired}</p>
              <Link to="/build/login" data-testid="build-confirm-signin-request-new-link" className="inline-block rounded-md bg-brand px-5 py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim transition-colors">
                Request a new link
              </Link>
            </div>
          ) : (
            <div className="mt-4">
              <h1 className="font-editorial text-2xl text-ink mb-4">Confirm sign-in</h1>
              <p className="text-sm text-ink-soft mb-7">Click below to finish signing in to NJM Build. This link is valid once, for 15 minutes.</p>
              <button
                onClick={confirm} disabled={state === "confirming" || state === "missing"}
                data-testid="build-confirm-signin-btn"
                className="w-full rounded-md bg-brand py-3 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
              >
                {state === "confirming" ? "Signing you in…" : "Confirm sign-in"}
              </button>
              {state === "missing" && (
                <p className="text-xs text-faint mt-4">
                  That link looks incomplete. <Link to="/build/login" className="font-semibold underline underline-offset-2">Request a new one</Link>.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
